import { Fade, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Dropdown from '@/components/GlobalNavigation/Dropdown/Dropdown';
import {
  getPosition,
  GlobalNavigationProps,
  NavPosition,
} from '@/components/GlobalNavigation/GlobalNavigation';
import useHelperNav from '@/components/GlobalNavigation/hooks/useHelperNav';
import useHideNav from '@/components/GlobalNavigation/hooks/useHideNav';
import useNavPosition from '@/components/GlobalNavigation/hooks/useNavPosition';
import useNavTransparency from '@/components/GlobalNavigation/hooks/useNavTransparency';
import NavBanner from '@/components/GlobalNavigation/NavBanner/NavBanner';
import useSharedStyles from '@/components/GlobalNavigation/SharedNav.styles';
import Link from '@/components/Link/Link';
import { getBaseUrlByEnv } from '@/constants/baseUrls';
import { FieldlessDropdownMenu } from '@/contentful/types';
import { userLocationInterface } from '@/hooks/geo';
import TextLogo from '@/public/cmsAssets/assets/img/logo/TextLogo';
import { trackEvents } from '@/utils/analytics';
import useIsScrolling from '@/utils/useIsScrolling';
import useScrollDirection from '@/utils/useScrollDirection';
import useScrollPosition from '@/utils/useScrollPosition';

import useStyles from './DesktopNav.styles';

interface DesktopNavProps extends GlobalNavigationProps, userLocationInterface {
  isOnDark: boolean;
}

export const HELPER_NAV_SCROLL_THRESHOLD = 350;
export const IMMERSIVE_NAV_SCROLL_THRESHOLD = 175;

export const getColorVariants = (
  isOnDark: boolean,
  isHelperNav: boolean,
  isNavTransparent: boolean,
  sharedStyles: ReturnType<typeof useSharedStyles>['classes'],
  cx: ReturnType<typeof useSharedStyles>['cx']
) => {
  // default to non immersive, not dark (light background)
  const variants = {
    navContainerClass: sharedStyles.lightOpaqueContainer,
    logoClass: sharedStyles.logoLinkOnLight,
    backgroundColor: sharedStyles.lightOpaqueContainer,
  };

  if (!isHelperNav) {
    const backgroundColor = cx({
      [sharedStyles.darkOpaqueContainer]: !isNavTransparent && isOnDark,
      [sharedStyles.lightOpaqueContainer]: !isNavTransparent && !isOnDark,
    });

    variants.navContainerClass = cx({
      [sharedStyles.onDarkText]: isOnDark,
      [sharedStyles.onLightText]: !isOnDark,
      [backgroundColor]: true,
    });

    variants.backgroundColor = backgroundColor;

    variants.logoClass = cx({
      [sharedStyles.logoLinkOnDark]: isOnDark,
      [sharedStyles.logoLinkOnLight]: !isOnDark,
    });
  } else {
    variants.navContainerClass = cx(
      sharedStyles.onLightText,
      sharedStyles.lightOpaqueContainer,
      sharedStyles.helperNavContainer
    );
    variants.logoClass = sharedStyles.logoLinkOnLight;
    variants.backgroundColor = sharedStyles.lightOpaqueContainer;
  }

  return variants;
};

const DesktopNav = ({
  categories,
  promoCta,
  loginCta,
  primaryCta,
  secondaryCta,
  immersive,
  userLocation,
  setUserLocation,
  isOnDark,
}: DesktopNavProps) => {
  const [transparentNavDisabled, setTransparentNavDisabled] =
    useState<boolean>(false);
  const [selectedNavItem, setSelectedNavItem] =
    useState<FieldlessDropdownMenu | null>(null);
  const [previousNavItem, setPreviousNavItem] =
    useState<FieldlessDropdownMenu | null>(null);
  const [hoverStateActive, setHoverStateActive] = useState<boolean>(false);
  const [navFocused, setNavFocused] = useState<boolean>(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const categoryHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrolling = useIsScrolling();
  const scrollPosition = useScrollPosition();
  const scrollDirection = useScrollDirection();
  const { typography } = useTheme();

  const [navPosition] = useNavPosition(
    immersive,
    scrollPosition,
    scrollDirection
  );
  const hideNav = useHideNav(
    scrollPosition,
    scrollDirection,
    hoverStateActive,
    navFocused
  );
  const showDropDown = hoverStateActive || navFocused;
  const [transparentNav, setTransparentNav] = useNavTransparency(
    immersive,
    scrollPosition,
    scrollDirection
  );
  const [isHelperNav, setIsHelperNav] = useHelperNav(
    scrollPosition,
    scrollDirection
  );

  // On static scroll, make dropdown dissapear
  useEffect(() => {
    if (hoverStateActive) {
      setHoverStateActive(false);
    }
    if (navFocused) {
      setNavFocused(false);
    }
  }, [scrollPosition]);

  // When a user stops scrolling and is focused on item, dropdown reappears
  useEffect(() => {
    if (!isScrolling && selectedNavItem) {
      setTimeout(() => {
        // wait a little to avoid flickering
        setHoverStateActive(true);
      }, 100);
    }
  }, [isScrolling]);

  // On initial load and refresh, set style if not at top of page.
  useEffect(() => {
    if (scrollPosition > HELPER_NAV_SCROLL_THRESHOLD) {
      setIsHelperNav(true);
    }

    if (immersive && scrollPosition > IMMERSIVE_NAV_SCROLL_THRESHOLD) {
      setTransparentNav(false);
    }
  });

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.offsetHeight);
    }
  }, []);

  const isNavTransparent = transparentNav && !transparentNavDisabled;

  // if nav is not immersive and position is fixed then we want to add placeholder to avoid shift from static to fixed.
  const enablePlaceholderToAvoidShift =
    navPosition == NavPosition.FIXED && !immersive;

  const handleNavHover = (isHovered: boolean) => {
    setTransparentNavDisabled(isHovered);
    setHoverStateActive(isHovered);
  };
  const { classes, cx } = useStyles();
  const { classes: sharedStyles } = useSharedStyles({
    useTransition: !navFocused,
  });

  const useDarkStyles = isOnDark && !isHelperNav;
  const { logoClass, navContainerClass } = getColorVariants(
    isOnDark,
    isHelperNav,
    isNavTransparent,
    sharedStyles,
    cx
  );

  const clearNav = () => {
    setSelectedNavItem(null);
    setPreviousNavItem(null);
    handleNavHover(false);
    setNavFocused(false);
  };

  return (
    <>
      {/* FIX TO SHIFTING CONTENT */}
      {enablePlaceholderToAvoidShift && (
        <div className={sharedStyles.placeholder} />
      )}
      <div
        id='global-nav'
        data-testid='nav-container'
        ref={containerRef}
        className={cx({
          [sharedStyles.navContainer]: true,
          [navContainerClass]: true,
        })}
        style={{
          position: getPosition(navPosition),
          top: navFocused || hideNav === false ? 0 : typography.pxToRem(-133),
          transition: !navFocused ? `top 0.5s` : '',
        }}
      >
        <div className={sharedStyles.bannerContainer}>
          <NavBanner
            userLocation={userLocation}
            setUserLocation={setUserLocation}
            promo={promoCta}
            login={loginCta}
            ctaContent={secondaryCta}
            isOnDark={useDarkStyles}
            isHelperNav={isHelperNav}
          />
        </div>
        <div
          className={cx(sharedStyles.navBody)}
          onMouseLeave={() => {
            setSelectedNavItem(null);
            handleNavHover(false);
            setPreviousNavItem(null);
          }}
        >
          <div className={sharedStyles.navItem}>
            <div className={sharedStyles.logoContainer}>
              <Link href={`${getBaseUrlByEnv()}`} target='_self' rel=''>
                <TextLogo classname={cx(logoClass, classes.logo)} width='121' />
              </Link>
            </div>
          </div>
          <div className={sharedStyles.navItem}>
            <div className={classes.navLinks}>
              {categories.map((item, index) => {
                return (
                  <div
                    onMouseEnter={() => {
                      // Adds a small timeout to avoid changing categories when moving mouse from category to T1
                      if (categoryHoverTimeoutRef.current) {
                        clearTimeout(categoryHoverTimeoutRef.current);
                      }

                      categoryHoverTimeoutRef.current = setTimeout(() => {
                        trackEvents([
                          {
                            event: 'engagement',
                            id: item.name,
                            nav_type: 'hover',
                          },
                        ]);
                        setPreviousNavItem(selectedNavItem);
                        setSelectedNavItem(item);
                        handleNavHover(true);
                      }, 100);
                    }}
                    // Prevents opening dropdown if mouse quickly passes over menu item
                    onMouseLeave={() => {
                      if (categoryHoverTimeoutRef.current) {
                        clearTimeout(categoryHoverTimeoutRef.current);
                      }
                    }}
                    onFocus={() => {
                      trackEvents([
                        {
                          event: 'engagement',
                          id: item.name,
                        },
                      ]);
                      setSelectedNavItem(item);
                      handleNavHover(true);
                      setNavFocused(true);
                    }}
                    onBlur={(e) => {
                      // clear the nav if we've lost focus on the T1's and the T2's entirely (by a click, for instance)
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        clearNav();
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    key={index}
                  >
                    <div
                      className={cx(
                        classes.navLink,
                        item === selectedNavItem && classes.navLinkSelected
                      )}
                      tabIndex={0}
                      data-testid={`navLink-${index}`}
                      role='button'
                    >
                      {item.name as string}
                    </div>
                    <Fade
                      in={selectedNavItem === item && showDropDown}
                      // Only fade in if the dropdown is not already open from hovering on a previous item
                      timeout={previousNavItem === null ? undefined : 0}
                      style={{ cursor: 'default' }}
                    >
                      <div>
                        <div
                          className={sharedStyles.dropdownContainer}
                          style={{
                            top:
                              navPosition === NavPosition.FIXED
                                ? typography.pxToRem(125)
                                : typography.pxToRem(
                                    containerHeight - scrollPosition
                                  ),
                            visibility:
                              selectedNavItem === item && showDropDown
                                ? 'visible'
                                : 'hidden',
                          }}
                        >
                          <Dropdown
                            dropdown={item}
                            isOnDark={isOnDark}
                            isHelperNav={isHelperNav}
                          />
                        </div>
                      </div>
                    </Fade>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={sharedStyles.navItem}>
            {primaryCta && (
              <div className={classes.buttonWrapper}>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  isOnDark={useDarkStyles}
                  label={primaryCta.text as string}
                  href={primaryCta.url as string}
                  gaEvent={{
                    event: 'engagement',
                    id: `button-${primaryCta.text}`,
                  }}
                  noPrefetchOnHover
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DesktopNav;
